// Sopwith 3D - computer pilot and autopilot.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// Ported from SDL Sopwith swauto.c. `aim()` is the heart of it: for each of
// the three possible elevator inputs it predicts one tick ahead, scores the
// result by distance to the goal, rejects options that would hit the ground
// or a building, and picks the best. The same routine steers enemy planes
// (attack, cruise) and the player's H-key autopilot (gohome).

import {
  ANGLES,
  BULLIFE,
  BULSPEED,
  CLOSE,
  HOME,
  MAX_THROTTLE,
  MAX_Y,
  NEAR,
  SYM_HGHT,
  SYM_WDTH,
} from './constants';
import { clampMax, clampRange, COS, iabs, imax, imin, inRange, movexy, setdxdy, SIN } from './fixed';
import type { Game } from './game';
import { copyobj } from './objects';
import { initcomp, initpln, initplyr, planeIsFlying, planeIsStalled } from './plane';
import { Obj, ObjState, ObjType } from './types';

/** Periodic wobble added to goals so planes do not fly dead straight. */
let courseadj = 0;

const scratchA = new Obj();
const scratchB = new Obj();
const xy = { x: 0, y: 0 };
const xy2 = { x: 0, y: 0 };

export function withinHomeRange(ob: Obj, x: number, y: number): boolean {
  const orig = ob.originalOb!;
  return iabs(x - orig.x) < HOME && iabs(y - ob.origY) < HOME;
}

function isTarget(ob: Obj): boolean {
  return ob.type === ObjType.TARGET || ob.type === ObjType.OX;
}

/**
 * Distance measure between two points: squared distance when close, and a
 * negative approximate distance when far, so "far" always sorts after "near".
 */
export function range(x: number, y: number, ax: number, ay: number): number {
  let dy = iabs(y - ay);
  dy += dy >> 1;
  let dx = iabs(x - ax);
  if (dx < 125 && dy < 125) {
    return dx * dx + dy * dy;
  }
  if (dx < dy) {
    const t = dx;
    dx = dy;
    dy = t;
  }
  return -((7 * dx + (dy << 2)) >> 3);
}

/**
 * Would a bullet fired now by `obs` hit `obt` within its lifetime? Returns 0
 * for no, 1 for a close hit, 2 for a hit late in the bullet's life (which
 * the AI prefers to answer with a missile).
 */
function shoot(g: Game, obt: Obj, obs: Obj): number {
  const bullet = scratchA;
  const target = scratchB;
  copyobj(bullet, obs);
  copyobj(target, obt);
  const bspeed = bullet.speed + BULSPEED;
  setdxdy(bullet, bspeed * COS(bullet.angle), bspeed * SIN(bullet.angle));
  bullet.x += SYM_WDTH / 2;
  bullet.y -= SYM_HGHT / 2;

  let nangle = target.angle;
  const nspeed = target.speed;
  let rprev = NEAR;

  for (let i = 0; i < BULLIFE; i++) {
    movexy(bullet, g.level.maxX, xy);
    if (target.state === ObjState.FLYING || target.state === ObjState.WOUNDED) {
      const r = target.flaps;
      if (r) {
        nangle = target.orient ? nangle - r : nangle + r;
        nangle = (nangle + ANGLES) % ANGLES;
        setdxdy(target, nspeed * COS(nangle), nspeed * SIN(nangle));
      }
    }
    movexy(target, g.level.maxX, xy2);
    const r = range(xy.x, xy.y, xy2.x, xy2.y);
    if (!inRange(0, r, rprev)) {
      return 0;
    }
    rprev = r;
    if (inRange(xy2.x, xy.x, xy2.x + SYM_WDTH - 1) && inRange(xy2.y - SYM_HGHT + 1, xy.y, xy2.y)) {
      return 1 + (i > BULLIFE / 3 ? 1 : 0);
    }
  }
  return 0;
}

/**
 * Would moving to (x, y) at altitude `alt` with vertical speed `dy` (x256)
 * crash into the ground or a building?
 */
function tstcrash2(g: Game, ob: Obj, x: number, y: number, alt: number, dy: number): boolean {
  if (alt > 50) {
    return false;
  }
  // When landing, only look one tick ahead or the autopilot circles forever.
  const lookahead = ob.home && withinHomeRange(ob, x, y) ? 1 : 3;
  if (alt + ((dy * lookahead) / 256) < 8) {
    return true;
  }

  const xl = imax(x - 32, ob.x - 32 - g.gmaxspeed);
  const xr = imin(x + 32, ob.x + 32 + g.gmaxspeed);

  let obt: Obj = ob;
  while (obt.xprev && obt.xprev.x >= xl) {
    obt = obt.xprev;
  }
  for (; obt.xnext; obt = obt.xnext) {
    if (!isTarget(obt) || obt.x < xl) {
      continue;
    }
    if (obt.x > xr) {
      return false;
    }
    const yt = obt.y + (obt.state === ObjState.STANDING ? 16 : 8);
    if (y <= yt) {
      return true;
    }
  }
  return false;
}

/** Planes are wingmen when they defend the same territory. */
function isWingman(plane: Obj, plane2: Obj): boolean {
  const o1 = plane.originalOb!;
  const o2 = plane2.originalOb!;
  const midpoint = ((o2.territoryL + o2.territoryR) / 2) | 0;
  return (
    plane !== plane2 && plane.faction === plane2.faction && inRange(o1.territoryL, midpoint, o1.territoryR)
  );
}

function findWingmen(g: Game, plane: Obj): { behind: Obj | null; ahead: Obj | null } {
  let behind: Obj | null = null;
  let ahead: Obj | null = null;
  if (!plane.target) {
    return { behind, ahead };
  }
  const direction = plane.target.x > plane.x ? 1 : -1;
  const isAheadOf = (other: Obj, p: Obj): boolean => other.x * direction >= p.x * direction;
  for (let i = 0; i < g.numPlanes; i++) {
    const w = g.planes[i];
    if (!isWingman(plane, w) || !planeIsFlying(w.state)) {
      continue;
    }
    if (isAheadOf(w, plane) && (!ahead || isAheadOf(ahead, w))) {
      ahead = w;
    }
    if (!isAheadOf(w, plane) && (!behind || isAheadOf(w, behind))) {
      behind = w;
    }
  }
  return { behind, ahead };
}

const CFLAPS = [0, -1, 1];

/**
 * Steer `ob` toward (ax, ay). If `obt` is given, also fire at it when a
 * shot would connect. `longway` marks a recursive detour so the approach
 * counter is not reset.
 */
export function aim(g: Game, ob: Obj, ax: number, ay: number, obt: Obj | null, longway: boolean): number {
  if (planeIsStalled(ob.state) && ob.angle !== (3 * ANGLES) / 4) {
    ob.flaps = -1;
    ob.accel = MAX_THROTTLE;
    return 0;
  }

  let x = ob.x;
  let y = ob.y;
  const dx = x - ax;

  const { behind, ahead } = findWingmen(g, ob);

  // Let the plane ahead in the formation take off first.
  if (ob.athome && ahead && iabs(ob.x - ahead.x) < 64) {
    return 0;
  }

  if (iabs(dx) <= 160) {
    if (!longway) {
      ob.hitcount = 0;
    }
  } else if (ahead && iabs(ob.x - ahead.x) < 80) {
    // Echelon: form up behind and below the leader.
    return aim(g, ob, ahead.x + 20, ahead.y - 20, null, false);
  } else {
    // Far away: if heading the wrong way, climb or dive to turn round;
    // otherwise cruise toward the goal at a safe height.
    if (ob.dx && dx < 0 === ob.dx < 0) {
      if (!ob.hitcount) {
        ob.hitcount = y > MAX_Y - 50 ? 2 : 1;
      }
      return aim(g, ob, x, ob.hitcount === 1 ? y + 25 : y - 25, null, true);
    }
    ob.hitcount = 0;
    let height = clampMax(y + 100, MAX_Y - 50 - courseadj);
    if (behind) {
      height = clampMax(height + 32, MAX_Y - 32);
    }
    return aim(g, ob, x + (dx < 0 ? 150 : -150), height, null, true);
  }

  // Close in: nudge straight onto the goal in the last few units.
  if (ob.speed) {
    const dy = y - ay;
    if (dy !== 0 && iabs(dy) < 6) {
      y += dy < 0 ? 1 : -1;
      ob.y = y;
    } else if (dx !== 0 && iabs(dx) < 6) {
      x += dx < 0 ? 1 : -1;
      ob.x = x;
      g.world.updateobjpos(ob);
    }
  }

  const obs = scratchA;
  copyobj(obs, ob);

  let nspeed = obs.speed + 1;
  if (nspeed > g.gmaxspeed && obs.type === ObjType.PLANE) {
    nspeed = g.gmaxspeed;
  } else if (nspeed < g.gminspeed) {
    nspeed = g.gminspeed;
  }

  const crange = [0, 0, 0];
  const ccrash = [false, false, false];
  const calt = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    const nangle = (obs.angle + (obs.orient ? -CFLAPS[i] : CFLAPS[i]) + ANGLES) % ANGLES;
    setdxdy(obs, nspeed * COS(nangle), nspeed * SIN(nangle));
    movexy(obs, g.level.maxX, xy);
    crange[i] = range(xy.x, xy.y, ax, ay);
    calt[i] = xy.y - g.level.ground[xy.x + 8];
    ccrash[i] = tstcrash2(g, ob, xy.x, xy.y, calt[i], nspeed * SIN(nangle));
    copyobj(obs, ob);
  }

  if (obt) {
    const i = shoot(g, obt, obs);
    if (i) {
      if (ob.missiles && g.conf.missiles && i === 2) {
        ob.mfiring = obt.athome ? ob : obt;
      } else {
        ob.firing = obt;
      }
    }
  }

  let rmin = 32767;
  let n = 0;
  for (let i = 0; i < 3; i++) {
    const r = crange[i];
    if (inRange(0, r, rmin - 1) && !ccrash[i]) {
      rmin = r;
      n = i;
    }
  }
  if (rmin === 32767) {
    rmin = -32767;
    for (let i = 0; i < 3; i++) {
      const r = crange[i];
      if (r < 0 && r > rmin && !ccrash[i]) {
        rmin = r;
        n = i;
      }
    }
  }

  if (ob.speed < g.gminspeed) {
    ob.accel = MAX_THROTTLE;
  }

  if (rmin === -32767) {
    // Every option crashes: throttle back and take whichever climbs most.
    if (ob.accel) {
      ob.accel--;
    }
    n = 0;
    let dy = calt[0];
    if (calt[1] > calt[0]) {
      dy = calt[1];
      n = 1;
    }
    if (calt[2] > dy) {
      n = 2;
    }
  } else if (ob.accel < MAX_THROTTLE) {
    ob.accel++;
  }

  if (ahead && iabs(ahead.x - ob.x) < 24) {
    ob.accel = imax(1, ahead.accel - 2);
  } else if (behind && iabs(behind.x - ob.x) > 32 && ob.dy === 0) {
    ob.accel = imax(1, behind.accel - 2);
  }

  ob.flaps = CFLAPS[n];
  if (ob.type === ObjType.PLANE && !ob.flaps && ob.speed) {
    ob.orient = ob.dx < 0 ? 1 : 0;
  }
  return 0;
}

/** Fly back to base and land; re-initialises the plane on arrival. */
export function gohome(g: Game, ob: Obj): number {
  if (ob.athome) {
    return 0;
  }
  courseadj = ((g.countmove & 0x001f) < 16 ? 1 : 0) << 4;
  if (withinHomeRange(ob, ob.x, ob.y)) {
    if (g.plyrplane) {
      initplyr(g, ob, ob.originalOb!);
    } else if (g.compplane) {
      initcomp(g, ob, ob.originalOb!);
    } else {
      initpln(g, ob, ob.originalOb!);
    }
    return 0;
  }
  // Wounded planes steer only every other tick.
  if (ob.state === ObjState.WOUNDED && (g.countmove & 1) !== 0) {
    return 0;
  }
  return aim(g, ob, ob.originalOb!.x, ob.origY, null, false);
}

/** Patrol the middle third of the territory at a safe height. */
function cruise(g: Game, ob: Obj): void {
  courseadj = ((g.countmove & 0x001f) < 16 ? 1 : 0) << 4;
  const orgx = ob.originalOb!.x;
  const maxX = g.level.maxX;
  aim(
    g,
    ob,
    courseadj + clampRange((maxX / 3) | 0, orgx, ((2 * maxX) / 3) | 0),
    MAX_Y - 50 - (courseadj >> 1),
    null,
    false,
  );
}

/** Chase a target: aim just behind it along its heading. */
function attack(g: Game, obp: Obj, ob: Obj): void {
  courseadj = ((g.countmove & 0x001f) < 16 ? 1 : 0) << 4;
  if (ob.speed) {
    aim(g, obp, ob.x - ((CLOSE * COS(ob.angle)) >> 8), ob.y - ((CLOSE * SIN(ob.angle)) >> 8), ob, false);
  } else {
    aim(g, obp, ob.x, ob.y + 4, ob, false);
  }
}

/** Per-tick decision for a computer plane. */
export function swauto(g: Game, ob: Obj): void {
  if (ob.target) {
    attack(g, ob, ob.target);
  } else if (!ob.athome) {
    cruise(g, ob);
  }
  ob.target = null;
}
